// frontend/src/services/sessionApi.js - Complete Session Management API
import api from './api';

export const sessionApi = {
  // ============================================================================
  // ADMIN SESSION MANAGEMENT
  // ============================================================================

  // Get all sessions with pagination and filters
  async getAllSessions(params = {}) {
    try {
      const response = await api.get('/admin/sessions', { params });
      return response.data;
    } catch (error) {
      console.error('Get all sessions error:', error);
      throw error;
    }
  },

  // Get session statistics for dashboard
  async getSessionStats() {
    try {
      const response = await api.get('/admin/sessions/stats');
      return response.data;
    } catch (error) {
      console.error('Get session stats error:', error);
      throw error;
    }
  },

  // Get detailed information about a specific session
  async getSessionDetails(sessionId) {
    try {
      const response = await api.get(`/admin/sessions/${sessionId}/details`);
      return response.data;
    } catch (error) {
      console.error('Get session details error:', error);
      throw error;
    }
  },

  // Terminate a specific session
  async terminateSession(sessionId, reason = 'admin_terminated') {
    try {
      const response = await api.post(
        `/admin/sessions/${sessionId}/terminate`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error('Terminate session error:', error);
      throw error;
    }
  },

  // Terminate all sessions for a specific user
  async terminateUserSessions(
    userId,
    userType,
    reason = 'admin_bulk_termination'
  ) {
    try {
      const response = await api.post(
        `/admin/users/${userId}/${userType}/sessions/terminate`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error('Terminate user sessions error:', error);
      throw error;
    }
  },

  // Export session report (CSV or JSON)
  async exportSessionReport(params = {}) {
    try {
      const queryParams = {
        format: params.format || 'csv',
        startDate: params.startDate,
        endDate: params.endDate,
      };

      if (params.format === 'csv') {
        // For CSV, we need to handle blob response
        const response = await api.get('/admin/sessions/export', {
          params: queryParams,
          responseType: 'blob',
        });

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `session-report-${
          new Date().toISOString().split('T')[0]
        }.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, message: 'Report downloaded successfully' };
      } else {
        // For JSON, return the data
        const response = await api.get('/admin/sessions/export', {
          params: queryParams,
        });
        return response.data;
      }
    } catch (error) {
      console.error('Export session report error:', error);
      throw error;
    }
  },

  // ============================================================================
  // USER SESSION MANAGEMENT (for current user)
  // ============================================================================

  // Get current user's sessions
  async getCurrentUserSessions() {
    try {
      const response = await api.get('/user/sessions');
      return response.data;
    } catch (error) {
      console.error('Get current user sessions error:', error);
      throw error;
    }
  },

  // Terminate other sessions (keep current one active)
  async terminateOtherSessions(reason = 'user_terminated_others') {
    try {
      const response = await api.post('/user/sessions/terminate-others', {
        reason,
      });
      return response.data;
    } catch (error) {
      console.error('Terminate other sessions error:', error);
      throw error;
    }
  },

  // Extend current session
  async extendCurrentSession() {
    try {
      const response = await api.post('/user/sessions/extend');
      return response.data;
    } catch (error) {
      console.error('Extend session error:', error);
      throw error;
    }
  },

  // Check current session status
  async getSessionStatus() {
    try {
      const response = await api.get('/user/sessions/status');
      return response.data;
    } catch (error) {
      console.error('Get session status error:', error);
      throw error;
    }
  },

  // Report suspicious activity
  async reportSuspiciousActivity(activityType, description, details = {}) {
    try {
      const response = await api.post('/user/sessions/report-suspicious', {
        activityType,
        description,
        details,
      });
      return response.data;
    } catch (error) {
      console.error('Report suspicious activity error:', error);
      throw error;
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  // Format session duration
  formatDuration(milliseconds) {
    if (!milliseconds) return '0h 0m';

    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  },

  // Format relative time
  formatRelativeTime(date) {
    if (!date) return 'Unknown';

    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  },

  // Get device icon based on type
  getDeviceIcon(deviceType) {
    switch (deviceType) {
      case 'mobile':
        return 'smartphone';
      case 'tablet':
        return 'tablet';
      case 'desktop':
      default:
        return 'monitor';
    }
  },

  // Get status color classes
  getStatusColorClass(status) {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      case 'suspicious':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },

  // Get location display name
  getLocationDisplay(location) {
    if (!location) return 'Unknown';

    if (location.city && location.country) {
      return `${location.city}, ${location.country}`;
    } else if (location.country) {
      return location.country;
    } else if (location.city) {
      return location.city;
    }

    return 'Unknown';
  },

  // Check if session is from office network
  isOfficeNetwork(ipAddress) {
    if (!ipAddress) return false;

    // Common office network patterns
    return (
      ipAddress.startsWith('192.168.') ||
      ipAddress.startsWith('10.0.') ||
      ipAddress.startsWith('172.16.')
    );
  },

  // Calculate security risk level
  getSecurityRiskLevel(securityScore, hasAlerts) {
    if (hasAlerts) return 'high';
    if (securityScore < 30) return 'high';
    if (securityScore < 60) return 'medium';
    return 'low';
  },

  // Get risk level color
  getRiskLevelColor(riskLevel) {
    switch (riskLevel) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  },

  // Format session data for display
  formatSessionForDisplay(session) {
    return {
      ...session,
      formattedDuration: this.formatDuration(session.duration),
      relativeLastActivity: this.formatRelativeTime(session.lastActivity),
      locationDisplay: this.getLocationDisplay(session.location),
      deviceIcon: this.getDeviceIcon(session.device?.type),
      statusColorClass: this.getStatusColorClass(session.status),
      isOfficeNetwork: this.isOfficeNetwork(session.location?.ip),
      riskLevel: this.getSecurityRiskLevel(
        session.securityScore,
        session.suspicious
      ),
      riskColorClass: this.getRiskLevelColor(
        this.getSecurityRiskLevel(session.securityScore, session.suspicious)
      ),
    };
  },

  // Bulk format sessions for table display
  formatSessionsForTable(sessions) {
    return sessions.map(session => this.formatSessionForDisplay(session));
  },

  // ============================================================================
  // SESSION MONITORING & ALERTS
  // ============================================================================

  // Set up session monitoring (for real-time updates)
  startSessionMonitoring(callback, interval = 30000) {
    const monitoringInterval = setInterval(async () => {
      try {
        const status = await this.getSessionStatus();
        callback(status);
      } catch (error) {
        console.warn('Session monitoring error:', error);
      }
    }, interval);

    return monitoringInterval;
  },

  // Stop session monitoring
  stopSessionMonitoring(intervalId) {
    if (intervalId) {
      clearInterval(intervalId);
    }
  },

  // Check if session is about to expire (within 30 minutes)
  isSessionExpiring(expiresAt) {
    if (!expiresAt) return false;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const thirtyMinutes = 30 * 60 * 1000;

    return expiry - now <= thirtyMinutes && expiry - now > 0;
  },

  // Get time until session expires
  getTimeUntilExpiry(expiresAt) {
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  },

  // ============================================================================
  // SESSION ANALYTICS HELPERS
  // ============================================================================

  // Group sessions by time period
  groupSessionsByPeriod(sessions, period = 'day') {
    const groups = {};

    sessions.forEach(session => {
      let key;
      const date = new Date(session.startTime);

      switch (period) {
        case 'hour':
          key = date.toISOString().substring(0, 13) + ':00:00.000Z';
          break;
        case 'day':
          key = date.toISOString().substring(0, 10);
          break;
        case 'week':
          const week = this.getWeekNumber(date);
          key = `${date.getFullYear()}-W${week}`;
          break;
        case 'month':
          key = date.toISOString().substring(0, 7);
          break;
        default:
          key = date.toISOString().substring(0, 10);
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(session);
    });

    return groups;
  },

  // Get week number for grouping
  getWeekNumber(date) {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  },

  // Calculate session statistics
  calculateSessionStats(sessions) {
    if (!sessions || sessions.length === 0) {
      return {
        total: 0,
        active: 0,
        expired: 0,
        suspicious: 0,
        averageDuration: 0,
        totalDuration: 0,
      };
    }

    const stats = {
      total: sessions.length,
      active: 0,
      expired: 0,
      suspicious: 0,
      totalDuration: 0,
    };

    sessions.forEach(session => {
      switch (session.status) {
        case 'active':
          stats.active++;
          break;
        case 'expired':
          stats.expired++;
          break;
        case 'suspicious':
          stats.suspicious++;
          break;
      }

      stats.totalDuration += session.duration || 0;
    });

    stats.averageDuration =
      stats.total > 0 ? stats.totalDuration / stats.total : 0;

    return stats;
  },

  // Get unique devices from sessions
  getUniqueDevices(sessions) {
    const devices = new Map();

    sessions.forEach(session => {
      if (session.device) {
        const key = `${session.device.name}-${session.location?.ip}`;
        if (!devices.has(key)) {
          devices.set(key, {
            ...session.device,
            lastSeen: session.lastActivity,
            sessionCount: 1,
            locations: [session.location],
          });
        } else {
          const device = devices.get(key);
          device.sessionCount++;
          if (new Date(session.lastActivity) > new Date(device.lastSeen)) {
            device.lastSeen = session.lastActivity;
          }
        }
      }
    });

    return Array.from(devices.values());
  },

  // Get unique locations from sessions
  getUniqueLocations(sessions) {
    const locations = new Map();

    sessions.forEach(session => {
      if (session.location && session.location.ip) {
        const key = session.location.ip;
        if (!locations.has(key)) {
          locations.set(key, {
            ...session.location,
            lastSeen: session.lastActivity,
            sessionCount: 1,
            isOffice: this.isOfficeNetwork(session.location.ip),
          });
        } else {
          const location = locations.get(key);
          location.sessionCount++;
          if (new Date(session.lastActivity) > new Date(location.lastSeen)) {
            location.lastSeen = session.lastActivity;
          }
        }
      }
    });

    return Array.from(locations.values());
  },
};
